import type { OnboardingStep } from '@cfl/shared';

/**
 * Click-by-click walkthroughs for the "Get started" steps. A guide only ever starts from the
 * checklist's arrow (`?guide=<step>`), never on its own.
 *
 * Each step points at an element by its `data-guide` name and moves on when the person does the
 * thing: `click` the target, `input` at least two characters into it, or press `next` (for a picker,
 * where "done" has no event of its own). The guide finishes only when the checklist's own data says
 * the step is done (`useGetStarted`) — never on a click alone.
 */
export interface GuideStep {
  target: string;
  title: string;
  body: string;
  advance: 'click' | 'input' | 'next';
}

export interface Guide {
  /** The pages this guide works on. Leaving them ends it, so it never follows someone elsewhere. */
  paths: string[];
  steps: GuideStep[];
  /** Shown once the step is really done. */
  success: string;
}

export const GUIDES: Record<OnboardingStep, Guide> = {
  PEOPLE_SET_UP: {
    paths: ['/hr/employees'],
    steps: [
      { target: 'needs-setup', title: 'Click “Needs setup”', body: 'It lists only the people still missing a department or a reporting manager.', advance: 'click' },
      { target: 'employee-actions', title: 'Open the ⋯ menu on a person', body: 'Pick anyone in the list.', advance: 'click' },
      { target: 'employee-edit', title: 'Click “Edit details”', body: 'Their details open in a form.', advance: 'click' },
      { target: 'employee-department', title: 'Choose their department', body: 'For example Operations. Then click Next.', advance: 'next' },
      { target: 'employee-manager', title: 'Choose their reporting manager', body: 'The person who will review them. Then click Next.', advance: 'next' },
      { target: 'employee-save', title: 'Click Save', body: 'Do the same for everyone else in the list — the step ticks when nobody is left.', advance: 'click' },
    ],
    success: 'Awesome! Everyone has a department and a reporting manager.',
  },
  KRA_GROUP: {
    paths: ['/hr/kra-groups', '/team/kra'],
    steps: [
      { target: 'kra-group-tab', title: 'Open the “Create Group” tab', body: 'A KRA group is one job’s weighting of KRAs.', advance: 'click' },
      { target: 'kra-group-new', title: 'Click “Create New KRA Group”', body: 'The group editor opens.', advance: 'click' },
      { target: 'kra-group-name', title: 'Name the group', body: 'Usually a job title, e.g. Technical Architect.', advance: 'input' },
      { target: 'kra-group-titles', title: 'Pick the job titles it applies to', body: 'Everyone with these titles gets their goals from this group. Then click Next.', advance: 'next' },
      { target: 'kra-group-kras', title: 'Add KRAs until the weightages total 100%', body: 'Pick a KRA, set its %, repeat. Then click Next.', advance: 'next' },
      { target: 'kra-group-save', title: 'Click “Create group”', body: 'We’ll tick the step as soon as it’s saved.', advance: 'click' },
    ],
    success: 'Awesome! Your first KRA group is ready.',
  },
  TEAM_GOALS: {
    paths: ['/team/goals'],
    steps: [
      { target: 'team-select-all', title: 'Tick everyone', body: 'The box at the top selects everyone you can act on.', advance: 'click' },
      { target: 'team-assign-group', title: 'No goals yet for someone? Assign a KRA group', body: 'Pick a group to give them goals. If everyone already has goals, just click Next.', advance: 'next' },
      { target: 'team-publish', title: 'Click “Publish selected”, then confirm', body: 'Their goals go live and they are emailed. The step ticks when every goal set is published.', advance: 'click' },
    ],
    success: 'Awesome! Your team’s goals are live.',
  },
  ONE_ON_ONE: {
    paths: ['/team/meetings'],
    steps: [
      { target: 'meeting-with', title: 'Choose who it’s with', body: 'One of your reports. Then click Next.', advance: 'next' },
      { target: 'meeting-when', title: 'Pick the date and time', body: 'Then click Next. The agenda is optional.', advance: 'next' },
      { target: 'meeting-schedule', title: 'Click “Schedule”', body: 'We’ll tick the step as soon as it’s booked.', advance: 'click' },
    ],
    success: 'Awesome! Your first 1-on-1 is booked.',
  },
  DEVELOPMENT_GOAL: {
    paths: ['/goals'],
    steps: [
      { target: 'dev-goal-title', title: 'Type a development goal', body: 'Something you want to learn or achieve, e.g. AWS certification.', advance: 'input' },
      { target: 'dev-goal-add', title: 'Click “Add”', body: 'It never changes your rating — your reviewers see it at appraisal.', advance: 'click' },
    ],
    success: 'Awesome! You added your first development goal.',
  },
  LOG_PROGRESS: {
    paths: ['/assessments'],
    steps: [
      { target: 'kra-row', title: 'Open a KRA', body: 'Click a KRA row to expand it.', advance: 'click' },
      { target: 'kra-comment', title: 'Write what you did', body: 'A line or two is enough; the band can wait. Then click Next.', advance: 'next' },
      { target: 'kra-save', title: 'Click “Save”', body: 'It stays a draft until you submit, so keep adding to it all quarter.', advance: 'click' },
    ],
    success: 'Awesome! You logged your first progress update.',
  },
  RECOGNITION: {
    paths: ['/recognition'],
    steps: [
      { target: 'recognition-give', title: 'Click “Recognise someone”', body: 'Thank a colleague for work you saw.', advance: 'click' },
      { target: 'recognition-to', title: 'Choose who', body: 'Then click Next.', advance: 'next' },
      { target: 'recognition-message', title: 'Say what they did', body: 'A sentence is plenty. Then click Next.', advance: 'next' },
      { target: 'recognition-send', title: 'Click Send', body: 'It never feeds anyone’s rating.', advance: 'click' },
    ],
    success: 'Awesome! You recognised your first colleague.',
  },
};

/** The checklist's link for a step, starting its walkthrough. */
export function guideHref(key: OnboardingStep, href: string): string {
  return `${href}${href.includes('?') ? '&' : '?'}guide=${key}`;
}

/** Whether `pathname` is one of the guide's pages. */
export function onGuidePage(guide: Guide, pathname: string): boolean {
  return guide.paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
